/**
 * Auth state — single source for the access token + current admin user.
 * Persisted to localStorage so a page refresh doesn't kick the user out.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Roles that can sign into this console. SUPER_ADMIN additionally manages admin accounts. */
export type AdminRole = 'ADMIN' | 'SUPER_ADMIN';

type AdminUser = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: AdminRole;
};

type AuthState = {
  access_token: string | null;
  refresh_token: string | null;
  user: AdminUser | null;
  set_session: (s: { access_token: string; refresh_token: string; user: AdminUser }) => void;
  clear: () => void;
};

/**
 * Read the role out of the access token.
 *
 * NOT a security check — the server enforces every gate and would 403 whatever
 * this returns. It decides which UI to render, and the token is the freshest
 * thing the client holds: `user` is a snapshot frozen at login, while the token
 * is reissued on each sign-in and always carries the current claim.
 *
 * This matters because the persisted snapshot goes stale. Promote an account to
 * SUPER_ADMIN in the database while its owner is signed in, and their stored
 * `user.role` still says ADMIN — so the Admins nav item stayed hidden and
 * /admins silently bounced to the dashboard, with nothing on screen explaining
 * why. The account was a super admin; the console just didn't know yet.
 *
 * A token issued before a promotion still says ADMIN, and that is correct: the
 * server would refuse those calls too until the user signs in again.
 */
function role_from_token(token: string | null): AdminRole | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))) as {
      role?: unknown;
    };
    return json.role === 'SUPER_ADMIN' || json.role === 'ADMIN' ? json.role : null;
  } catch {
    // Malformed or truncated token — fall back to the stored snapshot.
    return null;
  }
}

/**
 * Whether the signed-in admin may manage admin accounts.
 *
 * Prefers the token's claim, falling back to the stored user for sessions
 * persisted before `role` was recorded at all. An absent role reads as a plain
 * admin — hiding a control the user turns out to have is recoverable, showing
 * one they don't is not.
 */
export const use_is_super_admin = (): boolean =>
  use_auth_store((s) => (role_from_token(s.access_token) ?? s.user?.role) === 'SUPER_ADMIN');

export const use_auth_store = create<AuthState>()(
  persist(
    (set) => ({
      access_token: null,
      refresh_token: null,
      user: null,
      set_session: (s) => set(s),
      clear: () => set({ access_token: null, refresh_token: null, user: null }),
    }),
    { name: 'pet-marketplace-admin-auth' },
  ),
);
